import React, { useEffect, useRef, useState } from 'react';
import { FileText, User, Building2, Calendar, Lock, ChevronDown, ChevronRight, Trash2, Eye, Send, Download, History, MoreHorizontal, FlaskConical, Image, Pill, Stethoscope, Syringe, Scissors, ClipboardList, Pencil } from 'lucide-react';

const typeConfig = {
  'Lab Results':         { icon: FlaskConical,   gradient: 'from-blue-500 to-cyan-500',     bg: 'bg-blue-50',    text: 'text-blue-700',    border: 'border-blue-200' },
  'Imaging':             { icon: Image,          gradient: 'from-violet-500 to-purple-500',  bg: 'bg-violet-50',  text: 'text-violet-700',  border: 'border-violet-200' },
  'Prescriptions':       { icon: Pill,           gradient: 'from-emerald-500 to-green-500',  bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200' },
  'Consultation Notes':  { icon: Stethoscope,    gradient: 'from-amber-500 to-orange-500',   bg: 'bg-amber-50',   text: 'text-amber-700',   border: 'border-amber-200' },
  'Immunizations':       { icon: Syringe,        gradient: 'from-teal-500 to-cyan-500',      bg: 'bg-teal-50',    text: 'text-teal-700',    border: 'border-teal-200' },
  'Surgical Reports':    { icon: Scissors,       gradient: 'from-rose-500 to-pink-500',      bg: 'bg-rose-50',    text: 'text-rose-700',    border: 'border-rose-200' },
  'Discharge Summary':   { icon: ClipboardList,  gradient: 'from-slate-500 to-slate-600',    bg: 'bg-slate-50',   text: 'text-slate-700',   border: 'border-slate-200' },
  'Other':               { icon: FileText,       gradient: 'from-brand-500 to-indigo-500',   bg: 'bg-brand-50',   text: 'text-brand-700',   border: 'border-brand-200' },
};

const getTypeConfig = (type) => typeConfig[type] || typeConfig['Other'];

/** Purpose: Executes { record, selected = false, on toggle select, on delete, on view, on send, on download, on audit, on expand }. Inputs: { record, selected = false, onToggleSelect, onDelete, onView, onSend, onDownload, onViewAudit, onExpand }. Output: Operation result. */
export default function RecordCard({ record, selected = false, onToggleSelect, onDelete, onEdit, onView, onSend, onDownload, onViewAudit, onExpand }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [menuPlacement, setMenuPlacement] = useState('down');
  const menuRef = useRef(null);
  const moreButtonRef = useRef(null);
  const config = getTypeConfig(record.type);
  const TypeIcon = config.icon;

  useEffect(() => {
    /** Purpose: Closes menu when clicking outside action menu. Inputs: none. Output: cleanup function. */
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowMoreMenu(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /** Purpose: Handles toggle expand. Inputs: none. Output: Operation result. */
  const handleToggleExpand = () => {
    const newExpandedState = !isExpanded;
    setIsExpanded(newExpandedState);
    if (!newExpandedState) {
      setShowMoreMenu(false);
    }

    // Notify parent of expansion for audit logging
    if (onExpand) {
      onExpand(newExpandedState);
    }
  };

  /** Purpose: Toggles More menu and chooses up/down placement to avoid viewport clipping. Inputs: event. Output: void. */
  const handleToggleMoreMenu = (event) => {
    event.stopPropagation();
    const willOpen = !showMoreMenu;
    if (willOpen && moreButtonRef.current) {
      const rect = moreButtonRef.current.getBoundingClientRect();
      const estimatedMenuHeight = 180;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      if (spaceBelow < estimatedMenuHeight && spaceAbove > estimatedMenuHeight) {
        setMenuPlacement('up');
      } else {
        setMenuPlacement('down');
      }
    }
    setShowMoreMenu(willOpen);
  };

  return (
    <div className={`bg-white rounded-2xl shadow-card border border-slate-200/60 card-lift overflow-hidden ${selected ? 'ring-2 ring-brand-500 ring-offset-1' : ''}`}>
      <div
        onClick={handleToggleExpand}
        className="p-4 sm:p-5 cursor-pointer hover:bg-slate-50/50 transition-colors"
      >
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3 sm:gap-4 flex-1">
            {onToggleSelect && (
              <input
                type="checkbox"
                checked={selected}
                onChange={(e) => {
                  e.stopPropagation();
                  onToggleSelect(record.id);
                }}
                onClick={(e) => e.stopPropagation()}
                className="mt-1.5 sm:mt-2 w-4 h-4 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
                aria-label={`Select ${record.title}`}
              />
            )}
            <div className={`bg-gradient-to-br ${config.gradient} p-2.5 sm:p-3 rounded-xl shadow-sm`}>
              <TypeIcon className="w-4 h-4 sm:w-5 sm:h-5 text-white" />
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2.5 mb-2 flex-wrap">
                <h3 className="text-sm sm:text-base font-semibold text-slate-900 tracking-tight">{record.title}</h3>
                <span className={`px-2.5 py-0.5 ${config.bg} ${config.text} text-[11px] font-semibold rounded-full`}>
                  {record.type}
                </span>
                {record.encrypted && (
                  <span className="flex items-center gap-1 text-[11px] text-emerald-600 font-medium">
                    <Lock className="w-3 h-3" />
                    Encrypted
                  </span>
                )}
              </div>

              <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs sm:text-sm text-slate-500">
                <span className="flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5 text-slate-400" />
                  {record.provider}
                </span>
                <span className="flex items-center gap-1.5">
                  <Building2 className="w-3.5 h-3.5 text-slate-400" />
                  {record.facility}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-slate-400" />
                  {new Date(record.date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                </span>
              </div>

              {record.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-2.5">
                  {record.tags.map(tag => (
                    <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-500 text-[11px] font-medium rounded-md">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${isExpanded ? 'bg-slate-100' : 'hover:bg-slate-100'}`}>
              {isExpanded ? (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-400" />
              )}
            </div>
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-slate-100 bg-slate-50/50 p-4 sm:p-5 space-y-4 animate-fade-in">
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Description</p>
            <p className="text-sm text-slate-600 leading-relaxed">{record.description}</p>
          </div>

          <div className="text-sm">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1">Upload Date</p>
            <p className="text-slate-600">{new Date(record.uploadDate).toLocaleDateString()}</p>
          </div>

          <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 pt-2">
            <button
              onClick={(e) => {
                e.stopPropagation();
                onView(record);
              }}
              className="w-full sm:w-auto px-4 py-2 bg-gradient-to-r from-brand-600 to-violet-600 text-white rounded-xl text-sm font-medium hover:from-brand-700 hover:to-violet-700 transition-all shadow-sm hover:shadow-md flex items-center justify-center gap-2"
            >
              <Eye className="w-4 h-4" />
              View
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation();
                if (onSend) onSend(record);
              }}
              className="w-full sm:w-auto px-4 py-2 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-2"
            >
              <Send className="w-4 h-4" />
              Send
            </button>
            <div className="relative" ref={menuRef}>
              <button
                ref={moreButtonRef}
                onClick={handleToggleMoreMenu}
                className="w-full sm:w-auto px-3 py-2 bg-white border border-slate-200 text-slate-500 rounded-xl text-sm font-medium hover:bg-slate-50 hover:border-slate-300 transition-all flex items-center justify-center gap-1.5"
                aria-label="More record actions"
                aria-expanded={showMoreMenu}
              >
                <MoreHorizontal className="w-4 h-4" />
              </button>

              {showMoreMenu && (
                <div
                  className={`absolute right-0 w-40 sm:w-44 rounded-xl border border-slate-200/80 bg-white shadow-elevated z-30 p-1.5 animate-scale-in ${
                    menuPlacement === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
                  }`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    onClick={() => {
                      setShowMoreMenu(false);
                      if (onEdit) onEdit(record);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2.5 transition-colors"
                  >
                    <Pencil className="w-4 h-4" />
                    Edit
                  </button>
                  <button
                    onClick={() => {
                      setShowMoreMenu(false);
                      if (onViewAudit) onViewAudit(record);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2.5 transition-colors"
                  >
                    <History className="w-4 h-4" />
                    Activity
                  </button>
                  <button
                    onClick={() => {
                      setShowMoreMenu(false);
                      if (onDownload) onDownload(record);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-slate-50 hover:text-slate-900 flex items-center gap-2.5 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                  <div className="my-1 border-t border-slate-100" />
                  <button
                    onClick={() => {
                      setShowMoreMenu(false);
                      onDelete(record.id);
                    }}
                    className="w-full text-left px-3 py-2 rounded-lg text-sm text-red-600 hover:bg-red-50 flex items-center gap-2.5 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
